/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import throttle from 'lodash/throttle';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useParams } from 'js/lib/useRouter';
import { isSuperuser } from 'js/lib/user';

import { Grid } from '@coursera/cds-core';

import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import adsTracker from 'bundles/event-tracking/lib';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import { GetDegreeLandingPageContentDocument } from 'bundles/xddp/__generated__/contentfulOperations';
import type {
  DegreeLandingPageContentFragment,
  GetDegreeLandingPageContentQuery,
} from 'bundles/xddp/__generated__/contentfulTypes';
import Degree404 from 'bundles/xddp/components/Degree404';
import type { GetDegreeBySlug_Dedupe1Query } from 'bundles/xddp/components/__queries__/__generated__/getDegreeBySlug';
import getDegreeBySlug from 'bundles/xddp/components/__queries__/getDegreeBySlug.graphql';
import DLPBanner from 'bundles/xddp/components/landing/DLPBanner';
import DLPEmbeddedRegFormContainer from 'bundles/xddp/components/landing/DLPEmbeddedRegFormContainer';
import ProgramOverview from 'bundles/xddp/components/landing/ProgramOverview';
import AtAGlanceSection from 'bundles/xddp/components/shared/AtAGlanceSection';
import Container from 'bundles/xddp/components/shared/Container';
import SharedPremiumHeader from 'bundles/xddp/components/shared/SharedPremiumHeader';
import { usePropsFromRouter } from 'bundles/xddp/hooks/usePropsFromRouter';
import type { UserAgentProps } from 'bundles/xddp/types';
import createEoiCopyTest from 'bundles/xddp/utils/createEoiCopyTest';

const styles = {
  landingPage: css`
    .rc-EoiBranchContainer {
      background: none;
    }

    .landing-eoi-container {
      position: relative;
      z-index: 3;
      top: -120px;

      @media (max-width: 1024px) {
        top: 0;
        margin-bottom: 24px;
      }
    }

    .rc-EoiContainer {
      margin-top: 40px;
    }
  `,
};

type PropsFromDataFetcher = {
  pageContent: DegreeLandingPageContentFragment;
  degreeId: string;
};

type QueryProps = {
  pageContent: DegreeLandingPageContentFragment;
};

type PropsToComponent = UserAgentProps & PropsFromDataFetcher;

const LandingPage = ({ pageContent, userAgent, degreeId: id }: PropsToComponent) => {
  const [isStickyBarPresent, setIsStickyBarPresent] = useState(false);

  const { slug } = useParams();

  const scrollListener = useMemo(
    () =>
      throttle(() => {
        const headerBottom = document.querySelector('.header-container')?.getBoundingClientRect()?.bottom;
        if (!headerBottom) return;
        setIsStickyBarPresent(headerBottom < 0);
      }, 100),
    []
  );

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);
  }, [scrollListener]);

  if (!pageContent || !pageContent.slug || !pageContent.partnerInformation) {
    return <Degree404 />;
  }

  const { programName, programShortName, partnerInformation, title, programOverview, expressionOfInterest } =
    pageContent;
  const { partnerName, partnerLogo, bannerImage, primaryColor, secondaryColor } = partnerInformation;

  const trackDegreeApplyNow = () => {
    if (!pageContent) return;
    adsTracker.trackDegreeApplyNow(slug, id, programName, 'Degree');
  };

  const trackRequestInfo = () => {
    if (!pageContent) return;
    adsTracker.trackEmailMeInfo(slug, id, programName, 'Degree');
  };

  return (
    <div className="rc-LandingPage" css={styles.landingPage}>
      <CourseraMetatags
        metaNameAndProperties={{
          title: programName ?? undefined,
          description: `Explore the ${programName} Program offered by ${partnerName}`,
          absoluteImageHref: partnerLogo?.url ?? undefined,
          disableCrawlerIndexing: true,
        }}
        hrefLangOverrides={() => []}
      />
      {expressionOfInterest ? (
        <SharedPremiumHeader
          id={id}
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          slug={expressionOfInterest?.overrideProgramSlug ?? slug}
          partnerInformation={partnerInformation}
          programName={programName ?? ''}
          navigationElements={[]}
          onRequestInfoClick={trackRequestInfo}
          onApplyNowClick={trackDegreeApplyNow}
          isStickyBarPresent={isStickyBarPresent}
          isStickyEoiPresent={isStickyBarPresent}
          expressionOfInterestContainer={createEoiCopyTest(expressionOfInterest, slug)}
          userAgent={userAgent}
          currentNavElement={{}}
          isXddp
        />
      ) : (
        <DLPBanner
          userAgent={userAgent}
          title={title}
          partnerLogo={partnerLogo?.url}
          bannerImage={bannerImage?.url}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      )}
      <Container>
        <Grid container spacing={16}>
          {pageContent.expressionOfInterest && Boolean(pageContent.atAGlance) && (
            <Grid item xs={12}>
              {/*
                 // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
              <AtAGlanceSection {...pageContent.atAGlance} slug={slug} />
            </Grid>
          )}
          <Grid item md={!pageContent.expressionOfInterest ? 7 : 12} sm={12}>
            {!pageContent.expressionOfInterest && Boolean(pageContent.atAGlance) && (
              <Grid item xs={12}>
                {/*
                   // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
                <AtAGlanceSection {...pageContent.atAGlance} slug={slug} />
              </Grid>
            )}
            <ProgramOverview marketingContent={programOverview} />
          </Grid>
          {!pageContent.expressionOfInterest && (
            <Grid item md={5} sm={12}>
              <div className="landing-eoi-container">
                <DLPEmbeddedRegFormContainer
                  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                  slug={slug}
                  degreeId={id}
                  partnerName={partnerName}
                  shortName={programShortName}
                />
              </div>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

const LandingPageDataFetcher = () => {
  const { slug, versionParam, isPreview, locale } = usePropsFromRouter();

  const { data: pageContentData } = useQuery<GetDegreeLandingPageContentQuery, { slug: string; versionParam: string }>(
    GetDegreeLandingPageContentDocument,
    {
      // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      variables: { slug, versionParam: simplifyQueryValue(versionParam) ?? '' }, // TODO verify null/undefined case
      context: getGrowthCmsGraphqlContext(isPreview),
      errorPolicy: 'all',
    }
  );

  const { data: degreeBySlugData } = useQuery<
    GetDegreeBySlug_Dedupe1Query,
    { locale: string; slug: string; liveOnly: boolean }
  >(getDegreeBySlug, {
    // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    variables: { slug, locale, liveOnly: !isSuperuser() },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
  });

  const pageContent = pageContentData?.landingPageCollection?.items[0] || {};
  const degreeId = degreeBySlugData?.PremiumProductCollections?.getBySlug?.id.replace(/^degree~/, 'base~') || '';

  const Page = compose<PropsToComponent, PropsFromDataFetcher>(
    Retracked.createTrackedContainer(() => ({
      namespace: {
        app: 'dlp',
        page: 'dlp',
      },
    })),
    connectToStores<QueryProps, PropsToComponent>(['ApplicationStore'], ({ ApplicationStore }) => ({
      userAgent: ApplicationStore.getUserAgent(),
    }))
  )(LandingPage);

  return <Page pageContent={pageContent} degreeId={degreeId} />;
};

export default LandingPageDataFetcher;
